<template>
  <div></div>
</template>

<script setup lang="ts">
import * as Sentry from '@sentry/vue'
import { useWebSocket, useIdle } from '@vueuse/core'
import debounce from 'lodash/debounce'
const config = useRuntimeConfig()
const authStore = useAuthStore()

// Check if websocket URL is valid before attempting to connect
const websocketUrl = computed(() => config.public.websocketUrl || '')

const isConnected = ref(false)
const connectionAttempts = ref(0)
const maxReconnectAttempts = 5

// WebSocket options with better error handling and reconnection logic
const { status, send } = useWebSocket(websocketUrl, {
  heartbeat: {
    message: 'ping',
    interval: 5000, // Send heartbeat every 5 seconds
    pongTimeout: 5000, // Wait 5 seconds for pong response before considering connection dead
  },
  autoReconnect: {
    retries: maxReconnectAttempts,
    delay: 3000, // Longer delay (3 seconds) to allow more recovery time
    onFailed() {
      console.warn('WebSocket connection failed after max retries')
      // Log to Sentry as a message, not an exception
      Sentry.captureMessage(`WebSocket connection failed after ${maxReconnectAttempts} retries`, 'warning')
    },
  },
  onConnected(ws) {
    console.info('WebSocket connected successfully')
    isConnected.value = true
    connectionAttempts.value = 0
  },
  onDisconnected(ws, event) {
    console.info('WebSocket disconnected', event)
    isConnected.value = false

    // Don't log normal disconnects to Sentry
    if (event.code !== 1000) {
      Sentry.captureMessage(`WebSocket disconnected: Code ${event.code}`, 'info')
    }
  },
  onError(ws, event) {
    isConnected.value = false

    // Log details but don't throw an exception
    console.warn('WebSocket error:', {
      readyState: ws.readyState,
      url: ws.url,
      eventType: event.type,
      eventData: event,
    })
    Sentry.captureMessage(`WebSocket error: ${event.type}`, 'warning')

    if (connectionAttempts.value < maxReconnectAttempts) {
      connectionAttempts.value++
    }
  },
})

// Safe send wrapper function to prevent errors when not connected
const safeSend = (data: string) => {
  try {
    // Only attempt to send if we believe we're connected
    if (status.value === 'OPEN') {
      send(data)
    }
  } catch (err) {
    console.warn('Error sending WebSocket message:', err)
    // Log to Sentry as a message, not an exception
    Sentry.captureMessage(`Error sending WebSocket message: ${err}`, 'warning')
  }
}

const setAsOnline = debounce((leadId: string) => {
  if (!leadId) return
  safeSend(JSON.stringify({ status: 'online', leadId }))
}, 500)

const setAsOffline = debounce((leadId: string) => {
  if (!leadId) return
  safeSend(JSON.stringify({ status: 'offline', leadId }))
}, 500)

watch(
  () => [authStore.getUser?.leadId, authStore.getUser?.leadId],
  () => {
    if (!authStore.getUser?.leadId) return
    setAsOnline(authStore.getUser.leadId)
  },
  { immediate: true, deep: true }
)

// also set offline after 5 min inactivity
const { idle } = useIdle(5 * 60 * 1000)
watch(idle, (value: boolean) => {
  if (value && authStore.getUser?.leadId) {
    setAsOffline(authStore.getUser.leadId)
  } else if (authStore.getUser?.leadId) {
    setAsOnline(authStore.getUser.leadId)
  }
})

onMounted(() => {
  document.addEventListener('auth:logout', ((event: CustomEvent) => {
    if (event.detail?.leadId) {
      setAsOffline(event.detail.leadId)
    }
  }) as EventListener)
})

onUnmounted(() => {
  if (authStore.getUser?.id) {
    safeSend(JSON.stringify({ status: 'offline', userId: authStore.getUser.id }))
  }
})
</script>

<style scoped></style>
